import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import useApi from "../../../hooks/useApi";
import { validateStudentForm } from "../../../validation/validateStudentForm";
import {
  getStudentById,
  createStudent,
  updateStudent,
} from "../../../services/StudentService";
import { handleApiError } from "../../../utils/errorHandler";

export default function useStudent(studentId) {
  const navigate = useNavigate();

  const [fetchLoading, setFetchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [state, setState] = useState({});
  const [errors, setErrors] = useState({});

  const [images, setImages] = useState({
    profile_image: { file: null, preview: "" },
    father_profile_pic: { file: null, preview: "" },
    mother_profile_pic: { file: null, preview: "" },
    guardian_profile_pic: { file: null, preview: "" },
  });

  // Reset state when switching between create/edit modes
  useEffect(() => {
    if (!studentId) {
      setState({});
      setImages({
        profile_image: { file: null, preview: "" },
        father_profile_pic: { file: null, preview: "" },
        mother_profile_pic: { file: null, preview: "" },
        guardian_profile_pic: { file: null, preview: "" },
      });
      setErrors({});
    }
  }, [studentId]);

  const { execute: fetchStudent } = useApi(getStudentById, {
    showSuccess: false,
  });

  const { execute: createOrUpdateStudent } = useApi(
    studentId ? updateStudent : createStudent,
    {
      showSuccess: false, // We'll handle success message manually
      showError: true, // Let useApi handle error display
    },
  );

  // ---------------- Fetch Student ----------------
  const fetchStudentById = useCallback(async () => {
    if (!studentId) return;

    try {
      setFetchLoading(true);
      const res = await fetchStudent(studentId);

      if (res?.data?.data) {
        const data = res.data.data;

        setImages({
          profile_image: { file: null, preview: data.profile_image || "" },
          father_profile_pic: {
            file: null,
            preview: data.father_profile_pic || "",
          },
          mother_profile_pic: {
            file: null,
            preview: data.mother_profile_pic || "",
          },
          guardian_profile_pic: {
            file: null,
            preview: data.guardian_profile_pic || "",
          },
        });

        // Reset image fields so validation works on update
        data.profile_image = "";
        data.father_profile_pic = "";
        data.mother_profile_pic = "";
        data.guardian_profile_pic = "";

        const formatted = Object.fromEntries(
          Object.entries(data).map(([k, v]) => [k, v ?? ""]),
        );
        formatted.gender = formatted.gender?.toLowerCase() || "";
        setState(formatted);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setFetchLoading(false);
    }
  }, [studentId, fetchStudent]);

  useEffect(() => {
    fetchStudentById();
  }, [fetchStudentById]);

  // ---------------- Input Change ----------------
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setState((prev) => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  // ---------------- Image Change ----------------
  const handleImageChange = useCallback((e) => {
    const { name, files } = e.target;
    const file = files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];

    if (!validTypes.includes(file.type)) {
      setImages((prev) => ({
        ...prev,
        [name]: { file: null, preview: "" },
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: "Please upload a valid image file (jpg, jpeg, png)",
      }));
      return;
    }

    setImages((prev) => ({
      ...prev,
      [name]: { file, preview: URL.createObjectURL(file) },
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  // ---------------- Focus Handler ----------------
  const onFocusHandler = useCallback((e) => {
    const { name } = e.target;
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  // ---------------- Submit ----------------
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      // Create a copy for validation (don't mutate state)
      const cleanedFields = { ...state };

      // Prepare image fields for validation
      Object.keys(images).forEach((key) => {
        const file = images[key].file;
        const preview = images[key].preview;

        // For validation: use file if new file uploaded, otherwise use existing preview/URL
        if (file instanceof File) {
          cleanedFields[key] = file;
        } else {
          cleanedFields[key] = preview || cleanedFields[key] || "";
        }
      });

      const validationErrors = validateStudentForm(cleanedFields);

      if (Object.keys(validationErrors).length) {
        setErrors(validationErrors);

        const firstErrorField = Object.keys(validationErrors)[0];
        const el = document.querySelector(`[name="${firstErrorField}"]`);
        if (el) el.focus();

        return; // State remains unchanged - form values persist
      }

      // Build FormData for submission using cleanedFields (not state!)
      const formData = new FormData();

      // List of image field keys
      const imageFields = Object.keys(images);

      // Add ALL fields from cleanedFields to FormData
      Object.entries(cleanedFields).forEach(([key, value]) => {
        if (imageFields.includes(key)) {
          // For image fields: ONLY append if it's a File object
          // Never send URLs or empty strings - they fail Laravel file validation
          if (value instanceof File) {
            formData.append(key, value);
          }
          // If no file uploaded, skip this field entirely
        } else {
          // For non-image fields: append value or empty string
          formData.append(key, value ?? "");
        }
      });

      try {
        setSubmitLoading(true);

        // Call the correct API based on whether we're creating or updating
        const res = studentId
          ? await createOrUpdateStudent(studentId, formData) // Update: pass (id, data)
          : await createOrUpdateStudent(formData); // Create: pass (data) only

        // Check for successful response
        if (res?.data?.success && res?.data?.data) {
          enqueueSnackbar(
            studentId
              ? "Student updated successfully!"
              : "Student created successfully!",
            { variant: "success" },
          );
          navigate(`/student/update/${res.data.data.id}`);
        } else if (res?.data?.success === false) {
          console.error("API returned success: false", res.data);
          // Don't clear state - let user see their data
        }
      } catch (error) {
        console.error("Submit error:", error);
        console.error("Error response:", error?.response?.data);

        // handleApiError already called by useApi hook, don't call again
        // State remains unchanged - form values persist even on API errors
      } finally {
        setSubmitLoading(false);
      }
    },
    [state, images, studentId, createOrUpdateStudent, navigate],
  );

  return {
    fetchLoading,
    submitLoading,
    state,
    setState,
    errors,
    images,
    setImages,
    handleChange,
    handleImageChange,
    onFocusHandler,
    handleSubmit,
  };
}
